import * as React from 'react';
import { AiSurface } from '@/components/ui/ai-surface';
import { StatusChip } from '@/components/ui/status-chip';
import { FilterChip, TriCheckbox, WorkspaceSelect, linkishClass } from '@/components/ui/workspace';
import { ModuleSelect, type ModuleOption } from '@/components/requirement-import/module-select';
import type { DecisionInput, MatchDecision, MatchRow } from '@/lib/api/requirement-matches';
import { cx } from '@/lib/utils/cn';
import { CriticalityChip } from '../criticality';
import { asCriticality } from '../requirements-logic';
import {
  GROUP_LABEL,
  NOT_MAPPED_LABEL,
  VERDICT_TONE,
  isTicked,
  landsIn,
  libraryChoice,
  moduleFor,
  scoreText,
} from './match-logic';

/**
 * One spreadsheet row in the match review (T-0264; E14-design §2): on the
 * left what the client wrote and the library item the AI chose (or the
 * nearest one), with code's meaning score and the AI's one-sentence reason
 * on the AI surface; on the right what happens to the row. A Same row is a
 * tick box (preselected; unticking skips it). A Broader or No match row
 * offers Add the library item (any of its five candidates, linked to the
 * row in the client's wording), Add as written (in the module its client
 * area names, another module, or Not mapped) or Skip. An added row is
 * read-only.
 */

/** The `ModuleSelect` value of Not mapped. */
const NOT_MAPPED = '__not_mapped__';

const LABEL = 'text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500';

export interface MatchRowCardProps {
  row: MatchRow;
  /** False while the run is being matched, and for an added row. */
  editable: boolean;
  busy: boolean;
  focused: boolean;
  /** The candidate list is open (Enter on the focused row). */
  expanded: boolean;
  modules: ReadonlyArray<ModuleOption>;
  onExpandedChange: (open: boolean) => void;
  onDecide: (input: DecisionInput) => void;
  onFocus: () => void;
}

export const MatchRowCard = React.forwardRef<HTMLElement, MatchRowCardProps>(function MatchRowCard(
  { row, editable, busy, focused, expanded, modules, onExpandedChange, onDecide, onFocus },
  ref,
) {
  const pick = libraryChoice(row);
  const others = row.candidates.filter((c) => c.library_item_id !== pick?.library_item_id);
  const isSame = row.verdict === 'same';
  const shown = row.decision;
  const module = moduleFor(row);
  const name = row.client_ref ? `Row ${row.client_ref}` : `${row.sheet} row ${row.row_number}`;
  const pickScore = pick ? scoreText(pick) : null;
  const moduleChoices = React.useMemo<ModuleOption[]>(
    () => [{ id: NOT_MAPPED, name: NOT_MAPPED_LABEL }, ...modules],
    [modules],
  );

  const choose = (decision: MatchDecision) => {
    if (decision === 'add_library') {
      if (pick) onDecide({ decision, library_item_id: pick.library_item_id });
    } else if (decision === 'add_as_written') {
      onDecide(module ? { decision, module_id: module.id } : { decision });
    } else {
      onDecide({ decision });
    }
  };

  return (
    <article
      ref={ref}
      aria-label={`${name}: ${row.text}`}
      data-focused={focused || undefined}
      onClick={onFocus}
      className={cx(
        'grid grid-cols-[minmax(0,1fr)_300px] gap-5 rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3.5 max-[1100px]:grid-cols-1',
        row.requirement || row.ready ? 'border-l-posture-healthy' : 'border-l-ai-surface-border',
        focused && 'ring-2 ring-accent-amber-600',
      )}
    >
      <div className="flex min-w-0 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {isSame ? (
            <TriCheckbox
              state={isTicked(row) ? (editable ? 'on' : 'locked') : 'off'}
              aria-label={`Add ${name}`}
              disabled={!editable || busy}
              onChange={(next) => onDecide(next ? { decision: null } : { decision: 'skip' })}
            />
          ) : null}
          <span className="font-mono text-body-sm font-medium text-ink-900">
            {row.client_ref ?? `${row.sheet} ${row.row_number}`}
          </span>
          {row.client_area ? (
            <span className="text-meta text-ink-500">{row.client_area}</span>
          ) : null}
          <CriticalityChip value={asCriticality(row.criticality)} />
          <span className="flex-1" />
          {row.already_present ? (
            <StatusChip tone="healthy">Already on this engagement</StatusChip>
          ) : null}
          {row.verdict ? (
            <StatusChip tone={VERDICT_TONE[row.verdict]}>{GROUP_LABEL[row.verdict]}</StatusChip>
          ) : (
            <StatusChip tone="unassessed">Not matched yet</StatusChip>
          )}
        </div>
        <p className="text-body text-ink-900">{row.text}</p>
        {row.verdict ? (
          <AiSurface className="flex flex-col gap-1.5 rounded-card px-3 py-2.5">
            <p className={LABEL}>{row.chosen ? 'Library item' : 'Nearest library item'}</p>
            {pick ? (
              <p className="text-body-sm">
                <span className="font-mono font-medium">{pick.number}</span>
                <span className="text-meta"> · {pick.module_name}</span>
                {pickScore ? (
                  <span
                    className="text-meta"
                    title="Computed by code from the two texts, not by the AI. Below 0.62 a row cannot be Same; below 0.45 it is No match."
                  >
                    {' '}
                    · <span className="font-mono">{pickScore}</span>
                  </span>
                ) : null}
                <span className="mt-0.5 block">{pick.text}</span>
              </p>
            ) : (
              <p className="text-body-sm">No library item came close.</p>
            )}
            {row.reason ? <p className="text-meta">{row.reason}</p> : null}
          </AiSurface>
        ) : null}
        {others.length > 0 ? (
          <div>
            <button
              type="button"
              className={linkishClass()}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                onExpandedChange(!expanded);
              }}
            >
              {expanded ? 'Hide other candidates' : `Other candidates (${others.length})`}
            </button>
            {expanded ? (
              <ul className="mt-1.5 flex flex-col gap-2">
                {others.map((c) => (
                  <li
                    key={c.library_item_id}
                    className="flex items-start gap-2.5 rounded-chip border border-ink-100 bg-paper-50 px-2.5 py-2"
                  >
                    <span className="font-mono text-meta font-medium text-ink-700">{c.number}</span>
                    <span className="flex-1 text-body-sm text-ink-900">
                      {c.text}
                      <span className="block text-meta text-ink-500">
                        {c.module_name}
                        {scoreText(c) ? (
                          <>
                            {' '}
                            · <span className="font-mono">{scoreText(c)}</span>
                          </>
                        ) : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        {editable && !isSame ? (
          <>
            <div
              role="group"
              aria-label={`Decision for ${name}`}
              className="flex flex-wrap gap-1.5"
            >
              <FilterChip
                on={shown === 'add_library'}
                disabled={busy || !pick}
                onClick={() => choose('add_library')}
              >
                Add the library item
              </FilterChip>
              <FilterChip
                on={shown === 'add_as_written'}
                disabled={busy}
                onClick={() => choose('add_as_written')}
              >
                Add as written
              </FilterChip>
              <FilterChip on={shown === 'skip'} disabled={busy} onClick={() => choose('skip')}>
                Skip
              </FilterChip>
            </div>
            {shown === 'add_library' && row.candidates.length > 1 ? (
              <div>
                <label className={`${LABEL} mb-1.5 block`} htmlFor={`item-${row.id}`}>
                  Library item
                </label>
                <WorkspaceSelect
                  id={`item-${row.id}`}
                  className="h-8 text-body-sm"
                  value={pick?.library_item_id ?? ''}
                  disabled={busy}
                  onChange={(e) =>
                    onDecide({ decision: 'add_library', library_item_id: e.target.value })
                  }
                >
                  {row.candidates.map((c) => (
                    <option key={c.library_item_id} value={c.library_item_id}>
                      {c.number} · {c.text.length > 70 ? `${c.text.slice(0, 67)}…` : c.text}
                    </option>
                  ))}
                </WorkspaceSelect>
              </div>
            ) : null}
            {shown === 'add_as_written' ? (
              <div>
                <p className={`${LABEL} mb-1.5`}>Module</p>
                <ModuleSelect
                  aria-label={`Module for ${name}`}
                  value={row.decision_module?.id ?? NOT_MAPPED}
                  modules={moduleChoices}
                  allowSkip={false}
                  disabled={busy}
                  onChange={(id) =>
                    onDecide(
                      id === NOT_MAPPED
                        ? { decision: 'add_as_written' }
                        : { decision: 'add_as_written', module_id: id },
                    )
                  }
                />
                {row.decision_module ? null : (
                  <p className="mt-1 text-meta text-ink-500">
                    No module is named for this area, so it goes in {NOT_MAPPED_LABEL}. Map it here
                    or later from the requirements list.
                  </p>
                )}
              </div>
            ) : null}
          </>
        ) : null}
        <div>
          <p className={`${LABEL} mb-1`}>Lands in</p>
          <p className="text-body-sm text-ink-700">{landsIn(row)}</p>
          {row.decided_by_name && row.decision ? (
            <p className="mt-0.5 text-meta text-ink-500">Decided by {row.decided_by_name}</p>
          ) : null}
        </div>
      </div>
    </article>
  );
});
